import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Truck, Phone, Mail, Clock, Shield, Zap, MapPin, ArrowRight,
  CheckCircle2, Star, Package, ChevronDown, Users, CalendarCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1694715669993-ea0022b470f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxkZWxpdmVyeSUyMHZhbiUyMG1vdmluZyUyMGZ1cm5pdHVyZXxlbnwwfHx8fDE3NzY0NDY4MDl8MA&ixlib=rb-4.1.0&q=85";
const VAN_IMG = "https://images.unsplash.com/photo-1742858492775-8f58f645aa12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxkZWxpdmVyeSUyMHZhbiUyMG1vdmluZyUyMGZ1cm5pdHVyZXxlbnwwfHx8fDE3NzY0NDY4MDl8MA&ixlib=rb-4.1.0&q=85";
const LONDON_IMG = "https://images.unsplash.com/photo-1764416166626-7f146bd8e36d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxMb25kb24lMjBjaXR5JTIwc3RyZWV0JTIwdmFuJTIwdHJhbnNwb3J0fGVufDB8fHx8MTc3NjQ0NjgxNXww&ixlib=rb-4.1.0&q=85";

const FAQ_DATA = [
  {
    q: "How much does a man and van cost in London?",
    a: "Every man and van quote is generated instantly based on distance, vehicle and item requirements — calculated from your pickup and delivery postcodes. There are no hidden surcharges; the price you see at checkout is what you pay. Use the instant quote calculator for an exact price in seconds."
  },
  {
    q: "Can I book a same day man and van in London?",
    a: "Yes. Transport Anything 247 operates around the clock, 7 days a week. We regularly handle same day collections and deliveries across London. Simply request a quote online or call us directly on +44 7757 133163 and we will get your items moving as fast as possible."
  },
  {
    q: "What items can you transport with a man and van?",
    a: "We move almost anything: single items of furniture, boxes, appliances, pallets, office equipment, eBay or Gumtree purchases, and much more. If it fits in a van, we can move it. For larger items like vehicles or heavy machinery, we also have specialist transport options available."
  },
  {
    q: "Do you offer man and van services outside London?",
    a: "Absolutely. While we are based in London and cover every borough, we also provide nationwide man and van services. Whether you need a delivery from London to Manchester, Birmingham, Edinburgh, or anywhere else in the UK, we have you covered."
  },
  {
    q: "Is my delivery insured?",
    a: "Yes. All items transported by Transport Anything 247 are covered by our goods-in-transit insurance. This means your belongings are protected from the moment we collect them until they are delivered safely to the destination."
  },
  {
    q: "How do I book a man and van with Transport Anything 247?",
    a: "Booking is simple. Enter your pickup and delivery postcodes on our website to get an instant quote. Once you are happy with the price, fill in your details and pay securely online via Stripe. Alternatively, call us on +44 7757 133163 and we will arrange everything over the phone."
  }
];

const AREAS = [
  "Central London", "East London", "West London", "North London", "South London",
  "Hackney", "Camden", "Islington", "Greenwich", "Lewisham",
  "Croydon", "Bromley", "Ealing", "Hounslow", "Richmond",
  "Wandsworth", "Lambeth", "Southwark", "Tower Hamlets", "Barnet",
  "Enfield", "Harrow", "Brent", "Haringey", "Newham"
];

function FAQItem({ question, answer }) {
  return (
    <details className="group border border-slate-200 rounded-lg" data-testid="faq-item">
      <summary className="flex items-center justify-between cursor-pointer p-5 text-left font-semibold text-slate-900 hover:text-orange-600 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
        {question}
        <ChevronDown className="w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
      </summary>
      <div className="px-5 pb-5 text-slate-600 leading-relaxed">
        {answer}
      </div>
    </details>
  );
}

export default function ManAndVanLondonPage() {
  const navigate = useNavigate();

  const setMeta = useCallback((attr, attrVal, content) => {
    let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, attrVal);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
    el.setAttribute("data-page", "man-van-london");
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Title
    const pageTitle = "Man and Van London | Dedicated, Insured, Same-Day | Transport Anything 247";
    const originalTitle = document.title;
    document.title = pageTitle;
    // Re-apply after external scripts that may override
    const titleTimer = setTimeout(() => { document.title = pageTitle; }, 100);

    // Meta tags
    setMeta("name", "description", "Professional man and van London. Dedicated same-day collections, fully insured, 24/7. Instant transparent quotes online. Call +44 7757 133163 or book online now.");
    setMeta("name", "keywords", "man and van London, man with a van London, same day man and van London, man and van near me, professional man and van London");
    setMeta("property", "og:title", "Man and Van London | Transport Anything 247");
    setMeta("property", "og:description", "Professional man and van service in London. Dedicated same day collections, fully insured, with instant transparent quotes.");
    setMeta("property", "og:type", "website");

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://transportanything247.co.uk/services/man-and-van-london");
    canonical.setAttribute("data-page", "man-van-london");

    return () => {
      document.title = originalTitle;
      clearTimeout(titleTimer);
      document.querySelectorAll('[data-page="man-van-london"]').forEach(el => el.remove());
    };
  }, [setMeta]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Man and Van London",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Transport Anything 247",
      "telephone": "+447757133163",
      "email": "customers@trasportanything247.com",
      "url": "https://transportanything247.co.uk",
      "areaServed": {
        "@type": "City",
        "name": "London"
      },
      "openingHours": "Mo-Su 00:00-23:59"
    },
    "description": "Professional man and van London service. Dedicated same day collections, fully insured, with instant transparent quotes. Available 24/7 across all London boroughs and nationwide.",
    "areaServed": "London, United Kingdom",
    "serviceType": "Man and Van"
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_DATA.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  };

  return (
    <div className="min-h-screen" data-testid="man-van-london-page">
      {/* JSON-LD Structured Data injected directly */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }} />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2" data-testid="nav-logo">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                Transport Anything 247
              </span>
            </a>
            <div className="hidden md:flex items-center gap-6">
              <a href="/#calculator" className="nav-link">Get Quote</a>
              <a href="/#services" className="nav-link">Services</a>
              <a href="/#contact" className="nav-link">Contact</a>
              <a href="/blog" className="nav-link">Blog</a>
            </div>
            <a href="tel:+447757133163" className="hidden sm:flex items-center gap-2 text-orange-500 font-medium" data-testid="nav-phone">
              <Phone className="h-4 w-4" />
              <span>+44 7757 133163</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Man unloading boxes from delivery van in London" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-2xl">
            <p className="text-orange-400 font-medium text-sm tracking-wider uppercase mb-4">Reliable London Removals</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Man and Van <span className="text-orange-500">London</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed max-w-xl">
              Need a man with a van in London? Get an <strong className="text-white">instant transparent quote</strong> in seconds — dedicated same day collections, fully insured, available 24 hours a day, 7 days a week.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate("/")}
                className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-8 py-6 text-base rounded-md shadow-lg hover:shadow-xl active:scale-95 transition-all"
                data-testid="hero-get-quote-btn"
              >
                Get an Instant Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <a
                href="tel:+447757133163"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-medium px-8 py-3 rounded-md transition-colors"
                data-testid="hero-call-btn"
              >
                <Phone className="h-5 w-5" />
                Call Now
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-6 mt-10 text-sm text-slate-300">
              <span className="flex items-center gap-2"><Shield className="h-4 w-4 text-orange-400" /> Fully Insured</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-orange-400" /> 24/7 Available</span>
              <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-orange-400" /> Same Day Service</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-slate-900 py-5 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Star, label: "5-Star Rated Service" },
              { icon: Shield, label: "Goods-in-Transit Insurance" },
              { icon: Clock, label: "Available 24/7, 365 Days" },
              { icon: MapPin, label: "All London Boroughs" },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 text-slate-300 text-sm">
                <item.icon className="h-4 w-4 text-orange-500 flex-shrink-0" />
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Our Man and Van Services in London
            </h2>
            <p className="text-slate-600 text-base leading-relaxed">
              Whether you are moving a single item or an entire flat, our professional man and van London service handles it all with care and efficiency.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Package,
                title: "House Removals",
                desc: "Moving home in London? Our man and van teams handle everything from studio flats to multi-bedroom houses. We load, transport, and unload your belongings safely."
              },
              {
                icon: Truck,
                title: "Furniture Delivery",
                desc: "Bought furniture from IKEA, Gumtree, or Facebook Marketplace? We collect and deliver single items or full sets across London the same day."
              },
              {
                icon: CalendarCheck,
                title: "Same Day Collections",
                desc: "Need something moved urgently? Our same day man and van London service is available at short notice. Call us and we can often be with you within hours."
              },
              {
                icon: MapPin,
                title: "Office Relocations",
                desc: "Relocating your business? We handle office moves with minimal downtime, transporting desks, IT equipment, files, and furniture professionally."
              },
              {
                icon: Package,
                title: "eBay & Online Purchases",
                desc: "Bought something online that is too large for a courier? We collect and deliver items from private sellers, auction houses, and retail stores across London."
              },
              {
                icon: Users,
                title: "Student Moves",
                desc: "Moving to or from university accommodation in London? Our affordable man and van service is perfect for students relocating between term-time addresses."
              }
            ].map((service, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all group">
                <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-100 transition-colors">
                  <service.icon className="h-6 w-6 text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{service.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center text-slate-600 text-sm leading-relaxed max-w-2xl mx-auto">
            <p>
              We also offer specialist transport for <a href="/#services" className="text-orange-500 hover:underline font-medium">vehicles</a>, <a href="/#services" className="text-orange-500 hover:underline font-medium">pallets</a>, <a href="/#services" className="text-orange-500 hover:underline font-medium">containers</a>, and <a href="/#services" className="text-orange-500 hover:underline font-medium">heavy machinery</a>. Whatever you need moved, we have the right solution.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              How It Works
            </h2>
            <p className="text-slate-600 text-base">Book your man and van in London in three simple steps.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Get Your Quote", desc: "Enter your pickup and delivery postcodes on our website. Our calculator instantly shows you the total cost with no hidden fees." },
              { step: "02", title: "Book and Pay", desc: "Fill in your contact details and pay securely online. We accept all major cards through Stripe. Your booking is confirmed immediately." },
              { step: "03", title: "We Collect and Deliver", desc: "Our driver arrives at your pickup address, loads your items with care, and delivers them safely to the destination. It is that simple." },
            ].map((item, i) => (
              <div key={i} className="relative bg-white rounded-xl p-8 border border-slate-200 shadow-sm">
                <span className="text-6xl font-black text-slate-100 absolute top-4 right-6" style={{ fontFamily: 'var(--font-heading)' }}>{item.step}</span>
                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-white" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Why Choose Our Man and Van London Service?
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Transport Anything 247 is trusted by hundreds of customers across London every month. Here is why people choose us over other man and van services:
              </p>
              <div className="space-y-4">
                {[
                  { title: "Transparent Pricing", desc: "Our online quote calculator shows you the exact cost upfront. No surprises, no hidden fees. What you see is what you pay." },
                  { title: "Same Day Availability", desc: "We operate 24 hours a day, 7 days a week. Whether it is early morning or late evening, we are ready to help." },
                  { title: "Fully Insured", desc: "Every delivery is covered by our comprehensive goods-in-transit insurance, giving you complete peace of mind." },
                  { title: "Professional Drivers", desc: "Our vetted, experienced drivers treat your belongings with the same care as their own. Courteous, punctual, and reliable." },
                  { title: "Instant Online Booking", desc: "No waiting for callbacks. Get a quote, book, and pay in minutes through our secure online system." },
                  { title: "London-Wide Coverage", desc: "We cover every London borough plus nationwide delivery. From Barnet to Bromley, Ealing to Greenwich." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">{item.title}</h3>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src={VAN_IMG}
                alt="Delivery van ready for man and van London service"
                className="w-full h-96 object-cover rounded-xl shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white rounded-xl p-5 shadow-lg hidden md:block" data-testid="man-van-trust-badge">
                <p className="text-3xl font-bold" style={{ fontFamily: 'var(--font-mono)' }}>24/7</p>
                <p className="text-sm font-medium">Same-Day</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Areas We Cover in London
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Our man with a van London service covers every borough and postcode in the Greater London area. We also provide nationwide transport, so if you need items moved to or from London, we can handle that too.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {AREAS.map((area, i) => (
                  <span key={i} className="bg-white border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full">
                    {area}
                  </span>
                ))}
              </div>
              <p className="text-slate-500 text-sm">
                Do not see your area? No problem. We cover all London postcodes and nationwide. <a href="tel:+447757133163" className="text-orange-500 hover:underline font-medium">Call us</a> to confirm.
              </p>
            </div>
            <div>
              <img
                src={LONDON_IMG}
                alt="London street with vehicles and transport"
                className="w-full h-80 object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-14 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Ready to Book Your Man and Van?
          </h2>
          <p className="text-slate-300 mb-8 text-base max-w-2xl mx-auto">
            Get an instant quote in seconds. No registration required. Enter your postcodes, choose your item type, and see your price immediately.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/")}
              className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-10 py-6 text-base rounded-md shadow-lg hover:shadow-xl active:scale-95 transition-all"
              data-testid="cta-get-quote-btn"
            >
              Get Your Free Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a
              href="tel:+447757133163"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium px-10 py-3 rounded-md transition-colors border border-white/20"
              data-testid="cta-call-btn"
            >
              <Phone className="h-5 w-5" />
              +44 7757 133163
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600 text-base">Everything you need to know about our man and van London service.</p>
          </div>
          <div className="space-y-3">
            {FAQ_DATA.map((item, i) => (
              <FAQItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA with Contact */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="contact-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Get in Touch Today
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Have questions about our man and van service in London? Our team is available around the clock to help you plan your move or delivery. No job is too small or too large.
              </p>
              <div className="space-y-4">
                <a href="tel:+447757133163" className="flex items-center gap-4 group" data-testid="contact-phone">
                  <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                    <Phone className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="font-semibold text-slate-900">+44 7757 133163</p>
                  </div>
                </a>
                <a href="mailto:customers@trasportanything247.com" className="flex items-center gap-4 group" data-testid="contact-email">
                  <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                    <Mail className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-semibold text-slate-900">customers@trasportanything247.com</p>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                    <Clock className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Hours</p>
                    <p className="font-semibold text-slate-900">24/7 - Always Available</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>Quick Quote</h3>
              <p className="text-slate-600 text-sm mb-6">Use our instant calculator to get a price in under 30 seconds. No registration needed.</p>
              <Button
                onClick={() => navigate("/")}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-6 text-base rounded-md shadow-sm hover:shadow-md active:scale-95 transition-all"
                data-testid="final-get-quote-btn"
              >
                Go to Quote Calculator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <p className="text-center text-slate-400 text-xs mt-4">Instant prices. Secure payment via Stripe.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Block */}
      <section className="py-12 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Your Trusted Man and Van in London
          </h2>
          <div className="prose prose-slate prose-sm max-w-none text-slate-600 leading-relaxed space-y-4">
            <p>
              Finding a reliable man with a van in London should not be stressful. At Transport Anything 247, we have built our service around one simple idea: make moving easy, affordable, and stress-free. Whether you are relocating across the city, collecting a piece of furniture, or need a last-minute same day delivery, our team is ready to help.
            </p>
            <p>
              London is a busy city, and moving anything around it comes with its own challenges. Congestion zones, narrow streets, limited parking, and tight schedules can make deliveries complicated. That is why our drivers are experienced London locals who know the quickest routes and can navigate even the trickiest addresses with ease.
            </p>
            <p>
              Our pricing is straightforward and transparent. Every quote is calculated instantly based on distance, vehicle and item type, with no call-out fees, no hidden extras and no nasty surprises. The price you see on our quote calculator is the price you pay. We believe in honest, upfront pricing because that is what we would want as customers ourselves.
            </p>
            <p>
              From small <a href="/#services" className="text-orange-500 hover:underline">parcel deliveries</a> to full house moves, from <a href="/#services" className="text-orange-500 hover:underline">furniture transport</a> to <a href="/#services" className="text-orange-500 hover:underline">vehicle shipping</a>, Transport Anything 247 has you covered. We are more than just a man and van. We are your complete transport solution in London and across the UK.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Truck className="h-8 w-8 text-orange-500" />
                <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
                  Transport Anything 247
                </span>
              </div>
              <p className="text-slate-400 text-sm">
                Professional man and van service in London and across the UK. Affordable, reliable, available 24/7.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Our Services</h4>
              <div className="space-y-2">
                <a href="/#calculator" className="footer-link block">Get Quote</a>
                <a href="/services/man-and-van-london" className="footer-link block text-orange-400">Man and Van London</a>
                <a href="/services/car-transport-london" className="footer-link block">Car Transport London</a>
                <a href="/services/courier-service-london" className="footer-link block">Courier Service London</a>
                <a href="/services/furniture-delivery-london" className="footer-link block">Furniture Delivery London</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-slate-400 text-sm">
                <p>Phone: <a href="tel:+447757133163" className="text-orange-400 hover:text-orange-300">+44 7757 133163</a></p>
                <p>Email: <a href="mailto:customers@trasportanything247.com" className="text-orange-400 hover:text-orange-300">customers@trasportanything247.com</a></p>
                <p>Hours: 24/7 - Always Available</p>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Transport Anything 247. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
